import { useCallback, useState } from 'react';

const BOOKMARKS_KEY = 'finch-client-comms-bookmarks-v1';
const READ_STATE_KEY = 'finch-client-comms-read-v1';
const MAX_ITEMS_PER_MATTER = 500;

interface LocalStateData {
  [communicationId: string]: {
    id: string;
    timestamp: string;
  };
}

const loadFromStorage = (key: string, matterId: string): Set<string> => {
  try {
    const stored = localStorage.getItem(`${key}-${matterId}`);
    if (stored) {
      const parsed: LocalStateData = JSON.parse(stored);
      return new Set(Object.keys(parsed));
    }
  } catch (error) {
    console.error(`Failed to load ${key} from localStorage:`, error);
  }
  return new Set();
};

const saveToStorage = (
  key: string,
  matterId: string,
  ids: Set<string>,
): void => {
  try {
    const data: LocalStateData = {};
    const now = new Date().toISOString();
    ids.forEach((id) => {
      data[id] = { id, timestamp: now };
    });
    localStorage.setItem(`${key}-${matterId}`, JSON.stringify(data));
  } catch (error) {
    console.error(`Failed to save ${key} to localStorage:`, error);
  }
};

export const useClientCommunicationsLocalState = (matterId: string) => {
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(() =>
    loadFromStorage(BOOKMARKS_KEY, matterId),
  );

  const [readIds, setReadIds] = useState<Set<string>>(() =>
    loadFromStorage(READ_STATE_KEY, matterId),
  );

  const toggleBookmark = useCallback(
    (commId: string) => {
      setBookmarkedIds((prev) => {
        const next = new Set(prev);
        const wasBookmarked = next.has(commId);

        if (wasBookmarked) {
          next.delete(commId);
        } else {
          if (next.size >= MAX_ITEMS_PER_MATTER) {
            console.warn('Max bookmarks reached');
            return prev;
          }
          next.add(commId);
        }

        saveToStorage(BOOKMARKS_KEY, matterId, next);
        return next;
      });
    },
    [matterId],
  );

  const markAsRead = useCallback(
    (commId: string) => {
      setReadIds((prev) => {
        if (prev.has(commId)) return prev;

        const next = new Set(prev);
        next.add(commId);
        saveToStorage(READ_STATE_KEY, matterId, next);
        return next;
      });
    },
    [matterId],
  );

  const markAllAsRead = useCallback(
    (commIds: string[]) => {
      setReadIds((prev) => {
        const next = new Set(prev);
        let hasChanges = false;

        commIds.forEach((id) => {
          if (!next.has(id)) {
            next.add(id);
            hasChanges = true;
          }
        });

        if (hasChanges) {
          saveToStorage(READ_STATE_KEY, matterId, next);
        }
        return hasChanges ? next : prev;
      });
    },
    [matterId],
  );

  const isBookmarked = useCallback(
    (commId: string) => bookmarkedIds.has(commId),
    [bookmarkedIds],
  );

  const isRead = useCallback(
    (commId: string) => readIds.has(commId),
    [readIds],
  );

  return {
    bookmarkedIds,
    readIds,
    toggleBookmark,
    markAsRead,
    markAllAsRead,
    isBookmarked,
    isRead,
  };
};
